import React, { useEffect, useState } from 'react';
interface SundayCountdownTimerProps {
  onExpire?: () => void;
}
// Helper function to calculate next Sunday at 11:59 PM
const getNextSundayDeadline = () => {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  let daysUntilSunday = (7 - currentDay) % 7;
  // If it's Sunday, check if we're past 11:59 PM
  if (currentDay === 0) {
    const todayDeadline = new Date();
    todayDeadline.setHours(23, 59, 59, 999);
    if (now.getTime() < todayDeadline.getTime()) {
      // Still before 11:59 PM on Sunday, use today
      return todayDeadline.getTime();
    } else {
      // Past 11:59 PM on Sunday, go to next Sunday
      daysUntilSunday = 7;
    }
  }
  // If daysUntilSunday is 0 (and we're not on Sunday), it means we need next Sunday
  if (daysUntilSunday === 0) {
    daysUntilSunday = 7;
  }
  const nextSunday = new Date();
  nextSunday.setDate(now.getDate() + daysUntilSunday);
  nextSunday.setHours(23, 59, 59, 999);
  return nextSunday.getTime();
};
// Helper function to calculate time left
const calculateTimeLeft = (expiryTimestamp: number) => {
  const now = new Date().getTime();
  const distance = expiryTimestamp - now;
  if (distance <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }
  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
  const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
  const seconds = Math.floor(distance % (1000 * 60) / 1000);
  return {
    days,
    hours,
    minutes,
    seconds
  };
};
export function SundayCountdownTimer({
  onExpire = () => {}
}: SundayCountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
    // Initialize or get existing expiry time
    let expiryTime = localStorage.getItem('webinarReplyTimerExpiry');
    if (!expiryTime) {
      const deadline = getNextSundayDeadline();
      localStorage.setItem('webinarReplyTimerExpiry', deadline.toString());
      expiryTime = deadline.toString();
    }
    // Check if stored deadline has passed
    const storedDeadline = parseInt(expiryTime);
    if (storedDeadline < new Date().getTime()) {
      // Deadline passed, get new Sunday deadline
      const newDeadline = getNextSundayDeadline();
      localStorage.setItem('webinarReplyTimerExpiry', newDeadline.toString());
      expiryTime = newDeadline.toString();
    }
    // Set initial time immediately
    setTimeLeft(calculateTimeLeft(parseInt(expiryTime)));
    const updateTimer = () => {
      const expiryTimestamp = parseInt(expiryTime!);
      const newTimeLeft = calculateTimeLeft(expiryTimestamp);
      setTimeLeft(newTimeLeft);
      if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        // Timer expired, get next Sunday and update localStorage
        const newDeadline = getNextSundayDeadline();
        localStorage.setItem('webinarReplyTimerExpiry', newDeadline.toString());
        expiryTime = newDeadline.toString();
        onExpire();
      }
    };
    // Update every second
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [onExpire]);
  if (!isClient) {
    return <div className="text-center" data-id="element-380">
        <p className="text-base md:text-lg text-white mb-2 font-semibold" data-id="element-381">
          La oferta expira:
        </p>
        <p className="text-sm md:text-base text-red-400 mb-4 font-bold" data-id="element-382">
          Domingo 11:59 PM
        </p>
        <div className="flex items-center justify-center gap-2 md:gap-4" data-id="element-383">
          <TimeUnit value={0} label="Días" data-id="element-384" />
          <TimeUnit value={0} label="Horas" data-id="element-385" />
          <TimeUnit value={0} label="Min" data-id="element-386" />
          <TimeUnit value={0} label="Seg" data-id="element-387" />
        </div>
      </div>;
  }
  return <div className="text-center" data-id="element-388">
      <p className="text-base md:text-lg text-white mb-2 font-semibold" data-id="element-389">
        La oferta expira:
      </p>
      <p className="text-sm md:text-base text-red-400 mb-4 font-bold" data-id="element-390">
        Domingo 11:59 PM
      </p>
      <div className="flex items-center justify-center gap-2 md:gap-4" data-id="element-391">
        {timeLeft.days > 0 && <TimeUnit value={timeLeft.days} label="Días" data-id="element-392" />}
        <TimeUnit value={timeLeft.hours} label="Horas" data-id="element-393" />
        <TimeUnit value={timeLeft.minutes} label="Min" data-id="element-394" />
        <TimeUnit value={timeLeft.seconds} label="Seg" data-id="element-395" />
      </div>
    </div>;
}
function TimeUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-396">
      <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-lg w-14 h-14 md:w-20 md:h-20 flex items-center justify-center text-xl md:text-3xl font-bold shadow-lg" data-id="element-397">
        {value.toString().padStart(2, '0')}
      </div>
      <span className="text-xs md:text-sm text-gray-300 mt-1 md:mt-2 font-medium" data-id="element-398">
        {label}
      </span>
    </div>;
}